import React, { useState, useEffect } from "react";
import { Box, Image, Text, keyframes } from "@chakra-ui/react";
import { useColorModeValue } from "@chakra-ui/react";
import tesseraLogoFull from "../../assets/branding/tessera-logo-full.png";
import { PRODUCT_NAME } from "../../constants/brand";

interface LoadingScreenProps {
  message?: string;
}

// Smooth fade in
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Pulse animation for logo
const pulse = keyframes`
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.9;
    transform: scale(1.02);
  }
`;

// Shimmer effect
const shimmer = keyframes`
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
`;

// Progress bar fill
const progressFill = keyframes`
  from {
    width: 0%;
  }
  to {
    width: 100%;
  }
`;

// Blink animation for cursor
const blink = keyframes`
  0%, 50% {
    opacity: 1;
  }
  51%, 100% {
    opacity: 0;
  }
`;

const LoadingScreen: React.FC<LoadingScreenProps> = ({ message = "Loading..." }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const [bootLogs, setBootLogs] = useState<string[]>([]);
  const [startTime] = useState<number>(Date.now());

  const bgColor = useColorModeValue("#FFFFFF", "#FFFFFF"); // White
  const textColor = useColorModeValue("#3EEAEC", "#3EEAEC"); // Neon Aqua
  const accentColor = useColorModeValue("#38BBD1", "#38BBD1"); // Core Blue
  const mutedColor = useColorModeValue("rgba(62, 234, 236, 0.4)", "rgba(62, 234, 236, 0.4)");

  const loadingSteps = [
    "Getting all the ducks in a row...",
    "Counting money (virtually)...",
    "Polishing the crystal ball...",
    "Teaching AI to trade...",
    "Almost ready to make you rich...",
  ];

  // Boot log message templates (without timestamps)
  const bootLogTemplates = [
    { level: "INFO", module: "system.init", message: `Waking up ${PRODUCT_NAME} Dashboard from its beauty sleep [Coffee: Required]` },
    { level: "INFO", module: "config.loader", message: "Reading the secret recipe from /config/dashboard.json [Magic: Enabled]" },
    { level: "OK", module: "module.core", message: "All ducks successfully herded into formation (12/12) [Ducks: In a row]" },
    { level: "INFO", module: "db.connection", message: "Attempting to befriend database at postgresql://localhost:5432/tessera_db [Praying: Optional]" },
    { level: "OK", module: "db.connection", message: "Database friendship established [PID: 2847, Latency: 234ms, Luck: 100%]" },
    { level: "INFO", module: "auth.service", message: "Teaching AI how to recognize humans (it's harder than it sounds) [Security: Maximum]" },
    { level: "OK", module: "auth.service", message: "AI now knows you're human (probably) [Session: active, Hackers: Blocked]" },
    { level: "INFO", module: "trade.history", message: "Counting all the money you've made (or lost, no judgment) [Counting: Money]" },
    { level: "OK", module: "trade.history", message: "Found 1,247 trades worth of stories (2.3MB) in 222ms [Profit: Calculated]" },
    { level: "INFO", module: "market.feed", message: "Polishing crystal ball and connecting to future markets [Crystal: Polished]" },
    { level: "OK", module: "market.feed", message: "Crystal ball operational [Symbols: 150, Latency: 12ms, Future: Bright]" },
    { level: "INFO", module: "dashboard.launch", message: "Ready to make you rich (or at least look like it) [Success: Inevitable]" },
  ];

  // Format timestamp
  const formatTimestamp = (timestamp: number): string => {
    const date = new Date(timestamp);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    const milliseconds = String(date.getMilliseconds()).padStart(3, "0");
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${milliseconds}`;
  };

  useEffect(() => {
    // Calculate interval to spread steps evenly across loading time
    // MIN_LOADING_TIME is 5500ms, so we divide by number of steps
    const TOTAL_LOADING_TIME = 5500; // Match MIN_LOADING_TIME in ProtectedRoute.tsx
    const stepInterval = TOTAL_LOADING_TIME / loadingSteps.length;
    
    // Update loading steps evenly across the loading time
    const interval = setInterval(() => {
      setCurrentStep((prev) => {
        if (prev < loadingSteps.length - 1) {
          return prev + 1;
        }
        return prev;
      });
    }, stepInterval);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Simulate boot logs appearing sequentially with current timestamps
    const logInterval = setInterval(() => {
      setBootLogs((prev) => {
        if (prev.length < bootLogTemplates.length) {
          const logIndex = prev.length;
          const logTemplate = bootLogTemplates[logIndex];
          const logTimestamp = startTime + (logIndex * 400); // Increment by 400ms for each log
          const timestamp = formatTimestamp(logTimestamp);
          const levelPadded = logTemplate.level.padEnd(4, " ");
          const logLine = `[${timestamp}] ${levelPadded} [${logTemplate.module}] ${logTemplate.message}`;
          
          // Update progress based on boot logs
          const newProgress = ((prev.length + 1) / bootLogTemplates.length) * 100;
          setProgress(newProgress);
          
          return [...prev, logLine];
        } else {
          // All logs shown, set progress to 100%
          setProgress(100);
        }
        return prev;
      });
    }, 400);

    return () => clearInterval(logInterval);
  }, [startTime]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      minH="100vh"
      h="100vh"
      w="100%"
      bg={bgColor}
      position="fixed"
      top={0}
      left={0}
      right={0}
      bottom={0}
      overflow="hidden"
      border="none"
      outline="none"
      m={0}
      p={0}
      zIndex={9999}
      css={{
        margin: 0,
        padding: 0,
        border: "none",
        outline: "none",
      }}
    >
      {/* Subtle background gradient */}
      <Box
        position="absolute"
        top="0"
        left="0"
        w="100%"
        h="100%"
        css={{
          background: `radial-gradient(circle at 30% 50%, rgba(56, 187, 209, 0.1) 0%, transparent 50%),
                       radial-gradient(circle at 70% 50%, rgba(62, 234, 236, 0.08) 0%, transparent 50%)`,
        }}
        pointerEvents="none"
      />

      {/* Content */}
      <Box
        position="relative"
        zIndex={2}
        display="flex"
        flexDirection="column"
        alignItems="center"
        maxW="100%"
        w="100%"
        px={4}
        animation={`${fadeIn} 0.6s ease-out`}
      >
        {/* Logo */}
        <Box
          mb={12}
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          animation={`${pulse} 3s ease-in-out infinite`}
          w="100%"
        >
          <Image
            src={tesseraLogoFull}
            alt={`${PRODUCT_NAME} logo`}
            maxW="300px"
            w="auto"
            h="auto"
            objectFit="contain"
            filter="drop-shadow(0 0 20px rgba(62, 234, 236, 0.5)) drop-shadow(0 0 40px rgba(56, 187, 209, 0.3))"
          />
        </Box>

        {/* Loading text */}
        <Box
          as="div"
          color={textColor}
          fontSize="18px"
          fontFamily="'IBM Plex Sans', sans-serif"
          fontWeight="medium"
          mb={8}
          textAlign="center"
          letterSpacing="0.05em"
          textTransform="uppercase"
        >
          {loadingSteps[currentStep]}
        </Box>

        {/* Progress bar container */}
        <Box w="100%" maxW="400px" mb={6}>
          {/* Progress bar track */}
          <Box
            w="100%"
            h="2px"
            bg="rgba(62, 234, 236, 0.1)"
            borderRadius="full"
            overflow="hidden"
            position="relative"
          >
            {/* Progress bar fill */}
            <Box
              h="100%"
              w={`${progress}%`}
              css={{
                background: "linear-gradient(90deg, #38BBD1, #3EEAEC)",
                borderRadius: "full",
                transition: "width 0.6s ease-out",
                position: "relative",
                overflow: "hidden",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)",
                  animation: `${shimmer} 2s infinite`,
                },
              }}
            />
          </Box>
        </Box>

        {/* Progress percentage */}
        <Box
          as="div"
          color={mutedColor}
          fontSize="14px"
          fontFamily="'IBM Plex Sans', sans-serif"
          fontWeight="medium"
          letterSpacing="0.1em"
        >
          {Math.round(progress)}%
        </Box>

        {/* Subtle decorative elements */}
        <Box
          position="absolute"
          top="20%"
          left="10%"
          w="200px"
          h="200px"
          borderRadius="50%"
          css={{
            background: "radial-gradient(circle, rgba(62, 234, 236, 0.05) 0%, transparent 70%)",
            filter: "blur(40px)",
          }}
          pointerEvents="none"
        />
        <Box
          position="absolute"
          bottom="20%"
          right="10%"
          w="200px"
          h="200px"
          borderRadius="50%"
          css={{
            background: "radial-gradient(circle, rgba(56, 187, 209, 0.05) 0%, transparent 70%)",
            filter: "blur(40px)",
          }}
          pointerEvents="none"
        />
      </Box>

      {/* Terminal boot logs in bottom left */}
      <Box
        position="absolute"
        bottom="20px"
        left="20px"
        w="auto"
        maxW="500px"
        p={3}
        fontFamily="'IBM Plex Mono', 'Courier New', monospace"
        fontSize="11px"
        lineHeight="1.6"
        color="#3EEAEC"
        zIndex={0}
      >
        {bootLogs.map((log, index) => (
          <Box
            key={index}
            mb={1}
            css={{
              animation: `${fadeIn} 0.3s ease-out`,
            }}
          >
            {log}
          </Box>
        ))}
        {bootLogs.length > 0 && (
          <Box
            as="span"
            animation={`${blink} 1s infinite`}
          >
            |
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default LoadingScreen;
